import apiClient from './apiClient';

export interface VersionInfo {
  version: string;
  releaseDate: string;
}

const VERSION_STORAGE_KEY = 'app_version';
// バージョン情報をローカルストレージに保存
export const saveVersionInfo = (versionInfo: VersionInfo): boolean => {
  const storedInfo = getStoredVersionInfo();
  // 初回アクセス時（ストレージに情報がない場合）は保存のみ
  if (!storedInfo) {
    localStorage.setItem(VERSION_STORAGE_KEY, JSON.stringify(versionInfo));
    return false;
  }
  // 以前のバージョンと異なる場合かつ有効なバージョンの場合のみリロード
  if (storedInfo.version !== versionInfo.version &&
    storedInfo.version !== 'unknown' &&
    versionInfo.version !== 'unknown') {
    // 新しいバージョン情報を保存
    localStorage.setItem(VERSION_STORAGE_KEY, JSON.stringify(versionInfo));
    // バージョンに差異があることを返す
    return true;
  }
  // 変更がない場合は保存のみ
  localStorage.setItem(VERSION_STORAGE_KEY, JSON.stringify(versionInfo));
  return false;
};
export const getStoredVersionInfo = (): VersionInfo | null => {
  const stored = localStorage.getItem(VERSION_STORAGE_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored) as VersionInfo;
  } catch {
    return null;
  }
};
/**
 * バージョン情報を取得し、前回と異なる場合は自動リロードする
 * @param autoReload バージョンが異なる場合に自動的にリロードするかどうか
 */
export const fetchVersionInfo = async(autoReload: boolean = true): Promise<VersionInfo> => {
  try {
    const response = await apiClient.get('/health/');
    const headers = response.headers;
    const apiVersion =
      headers['x-api-version'] ||
      headers['X-API-Version'];
    const apiReleaseDate =
      headers['x-api-release-date'] ||
      headers['X-API-Release-Date'];
    if (apiVersion) {
      const versionInfo: VersionInfo = {
        version: apiVersion,
        releaseDate: apiReleaseDate || new Date().toISOString()
      };
      // バージョン情報を保存し、変更があるかを確認
      const hasChanged = saveVersionInfo(versionInfo);
      // バージョンに変更があり、自動リロードが有効な場合はページをリロード
      if (hasChanged && autoReload) {
        // 即時リロードではなく、少し遅延させる
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        // リロード後は実行されないが、一応値を返す
        return versionInfo;
      }
      return versionInfo;
    }
    // APIからバージョン情報を取得できなかった場合
    const storedInfo = getStoredVersionInfo();
    if (storedInfo) {
      return storedInfo;
    }
    // ストレージにも情報がない場合はデフォルト値
    return {
      version: 'unknown',
      releaseDate: new Date().toISOString()
    };
  } catch (error) {
    // エラー時はストレージから取得を試みる
    const storedInfo = getStoredVersionInfo();
    if (storedInfo) {
      return storedInfo;
    }
    // ストレージにも情報がない場合はデフォルト値
    return {
      version: 'unknown',
      releaseDate: new Date().toISOString()
    };
  }
};
/**
 * テスト用：バージョンを手動で変更してリロードをテストする
 * 開発環境でのみ使用
 */
export const testVersionChange = (newVersion: string): void => {
  // 現在のバージョン情報を取得
  const currentInfo = getStoredVersionInfo();
  if (!currentInfo) {
    return;
  }
  // ローカルストレージを新しいバージョンで更新
  localStorage.setItem(VERSION_STORAGE_KEY, JSON.stringify({
    ...currentInfo,
    version: newVersion
  }));
};
